import { shallowEqual, useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'
import { Col } from 'react-styled-flexboxgrid'
import { FlexItem } from 'styled-flex-component'

import { FullHr } from '@components/UI/Full/Full.styled'
import { PricePanelStyled, PricePanelWrapper } from '@components/UI/PricePanel/PricePanel.styled'
import { ConnectedTrackedRoute } from '@components/UI/TrackedRoute'
import { IMultiframeContextProps } from '@context/MultiframeControls'
import { EventTrackingNames, LicenseStatuses, SupportedSubmissionTypes } from '@enums'
import { withMultiframeControls } from '@hocs'
import hooks from '@hooks'
import { IApplicationState } from '@store'
import { selectIsPriceHiddenForCollaborator, selectIsSocialCampaign } from '@store/campaigns'
import {
  selectShouldShowLicenseOptionsPrice,
  selectShouldShowLicensePrice,
  selectShouldShowLicenseRelatedPrice,
  selectTransactedLicense,
} from '@store/mediaObjects'
import { selectCampaignId } from '@store/router'
import { selectActiveSubmissionMediaObject, selectSubmission } from '@store/submissions'
import { Container } from '@tribegroup'
import { getDisplayedSubmissionStatus, isEmpty } from '@utils'
import SubmissionCardPriceSummary from '../PriceSummary'
import SubmissionCardPricePanelAmount from './Amount'
import SubmissionCardPricePanelLabel from './Label'
import { SubmissionCardPriceSumaryWrapper } from './PricePanel.styled'

type Props = {
  submissionType: SupportedSubmissionTypes
  submissionId: number
} & IMultiframeContextProps

const SubmissionCardPricePanel: React.FC<Props> = ({
  submissionType,
  submissionId,
  activeFrameNumber,
}) => {
  const match = useRouteMatch()
  const campaignId = selectCampaignId(match) as number

  const { toggleIsVisible, toggleHide, toggle } = hooks.useToggle()

  const {
    isPriceHidden,
    displayedStatus,
    hasSubmission,
    mediaObjectId,
    showLicenseOptionsPrice,
    showLicensePrice,
    submission,
  } = useSelector((state: IApplicationState) => {
    const submission = selectSubmission(state, submissionId)
    const isSocial = selectIsSocialCampaign(state, campaignId)

    const mediaObject = selectActiveSubmissionMediaObject(state, submissionId, activeFrameNumber)

    const mediaObjectId = mediaObject?.id

    const transactedLicense = selectTransactedLicense(state, mediaObjectId)
    const showLicenseOptionsPrice = selectShouldShowLicenseOptionsPrice(
      state,
      mediaObjectId,
      isSocial,
    )
    const showLicensePrice = selectShouldShowLicensePrice(state, mediaObjectId, isSocial)
    const showLicenseRelatedPrice = selectShouldShowLicenseRelatedPrice(
      state,
      mediaObjectId,
      isSocial,
    )

    const displayedStatus = getDisplayedSubmissionStatus(
      submission,
      showLicenseRelatedPrice,
      transactedLicense.status as LicenseStatuses,
    )

    return {
      showLicensePrice,
      showLicenseOptionsPrice,
      mediaObjectId,
      displayedStatus,
      submission,
      hasSubmission: !isEmpty(submission),
      isPriceHidden: selectIsPriceHiddenForCollaborator(state, campaignId),
    }
  }, shallowEqual)

  const isSummaryAvailable = true

  const disableTracking = toggleIsVisible || isPriceHidden || !isSummaryAvailable
  const hasPrice =
    !isEmpty(submission.total_amount_before_tax_cents) || !isEmpty(submission.minimum_amount_cents)

  const showAmount = hasSubmission && hasPrice && !isPriceHidden

  const panelOnClick = () => {
    return isPriceHidden || !showAmount ? undefined : toggle()
  }

  const showHr = showAmount || displayedStatus

  return (
    <>
      {showHr && <FullHr />}
      <Container data-cy-element="pop-over-license-options">
        {toggleIsVisible && (
          <SubmissionCardPriceSumaryWrapper>
            <SubmissionCardPriceSummary
              mediaObjectId={mediaObjectId}
              campaignId={campaignId}
              submissionId={submissionId}
              onHide={toggleHide}
            />
          </SubmissionCardPriceSumaryWrapper>
        )}
        <ConnectedTrackedRoute
          disableTracking={disableTracking}
          eventName={EventTrackingNames.ViewPriceSummary}
          submissionId={submissionId}
          campaignId={campaignId}
        >
          <div
            onClick={isSummaryAvailable ? panelOnClick : undefined}
            role={isSummaryAvailable ? 'button' : undefined}
            data-testid="price-panel-btn"
          >
            <PricePanelStyled clickable={showAmount && isSummaryAvailable}>
              <PricePanelWrapper justifyBetween alignCenter>
                <FlexItem>
                  {showAmount && (
                    <Container data-testid="submission-price-panel-amount" minHeight={1.25}>
                      <SubmissionCardPricePanelAmount
                        campaignId={campaignId}
                        showLicensePrice={showLicensePrice}
                        showLicenseOptionsPrice={showLicenseOptionsPrice}
                        mediaObjectId={mediaObjectId}
                        submissionId={submissionId}
                        submissionType={submissionType}
                      />
                    </Container>
                  )}
                </FlexItem>
                {displayedStatus && (
                  <FlexItem noShrink>
                    <SubmissionCardPricePanelLabel displayedStatus={displayedStatus} />
                  </FlexItem>
                )}
              </PricePanelWrapper>
            </PricePanelStyled>
          </div>
        </ConnectedTrackedRoute>
      </Container>
      {showHr && (
        <Col xs={12} md={false}>
          <FullHr />
        </Col>
      )}
    </>
  )
}

export default withMultiframeControls(SubmissionCardPricePanel)
